import { useEffect, useMemo, useRef } from 'react';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import type { ProductId } from '@/constants/medicalOnDemand';
import { segmentClient } from '@/util/analytics';

type ProductSupportTeamLike = {
  supportedProducts: { id: string }[];
};

type SetValue = (
  name: 'productIds' | 'message',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  options?: { shouldDirty?: boolean }
) => void;

type UseProductSupportSuccessEntryParams = {
  pharmaProductSupportTeam?: ProductSupportTeamLike;
  prefillProductId?: string;
  prefillMessage: string;
  prefillSource?: string;
  currentProductIds?: string[];
  setValue: SetValue;
};

type UseProductSupportSuccessEntryResult = {
  source: string;
  initialPrefillProductId: string | null;
};

export function useProductSupportSuccessEntry(
  params: UseProductSupportSuccessEntryParams
): UseProductSupportSuccessEntryResult {
  const {
    pharmaProductSupportTeam,
    prefillProductId,
    prefillMessage,
    prefillSource,
    currentProductIds,
    setValue,
  } = params;

  const source = useMemo(() => prefillSource || 'direct_mod_form', [prefillSource]);

  const initialPrefillProductIdRef = useRef<string | null>(
    typeof prefillProductId === 'string' ? prefillProductId : null
  );

  const hasTrackedProductChangeRef = useRef(false);

  const previousProductIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const isProductSupportSuccessEntry = source === 'product_support_success';

    const hasPrefillPayload =
      isProductSupportSuccessEntry && (!!prefillProductId || !!prefillMessage);

    if (!pharmaProductSupportTeam || !hasPrefillPayload) {
      return;
    }

    const supportedProductIds = new Set(
      pharmaProductSupportTeam.supportedProducts.map((product) => product.id)
    );

    const canPrefillProduct = !!prefillProductId && supportedProductIds.has(prefillProductId);

    if (canPrefillProduct) {
      setValue('productIds', [prefillProductId as ProductId], { shouldDirty: true });
    }

    if (prefillMessage) {
      setValue('message', prefillMessage, { shouldDirty: true });
    }
  }, [pharmaProductSupportTeam, prefillProductId, prefillMessage, source, setValue]);

  useEffect(() => {
    const currentProductId = currentProductIds?.[0];

    const previousProductId = previousProductIdRef.current;

    if (previousProductId === undefined) {
      previousProductIdRef.current = currentProductId;

      return;
    }

    const initialPrefillProductId = initialPrefillProductIdRef.current;

    if (
      !hasTrackedProductChangeRef.current &&
      initialPrefillProductId &&
      previousProductId !== currentProductId &&
      currentProductId &&
      currentProductId !== initialPrefillProductId
    ) {
      hasTrackedProductChangeRef.current = true;

      segmentClient.track(ANALYTICS_EVENTS.MOD_PRODUCT_SUPPORT_SUCCESS_PRODUCT_CHANGED, {
        source,
        prefillProductId: initialPrefillProductId,
        fromProductId: previousProductId || null,
        toProductIds: currentProductIds,
      });
    }

    previousProductIdRef.current = currentProductId;
  }, [currentProductIds, source]);

  return {
    source,
    initialPrefillProductId: initialPrefillProductIdRef.current,
  };
}
